import { useState, useEffect, memo } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { yupResolver } from '@hookform/resolvers/yup';
import client from '../../../client';
import * as yup from 'yup';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import FuseMessage from '@fuse/core/FuseMessage';
import { useDispatch } from 'react-redux';
import { showMessage } from 'app/store/fuse/messageSlice';
import { Autocomplete, Typography } from '@mui/material';
import new_store from 'app/store';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const schema = yup.object().shape({
  name: yup.string().required().nullable(false),
});

function ModalCreateMaterials({
  isOpenModal,
  setIsOpenModal,
  getAllMaterials,
  info,
  setInfo,
}) {
  const history = useHistory();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(isOpenModal);
  const [createCategorie, setCreateCategorie] = useState(false);
  const [categories, setCategories] = useState([]);
  const isEdit = Boolean(info?.mt_id || info?.value);

  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      name: '',
      description: '',
      category: [],
    },
    resolver: yupResolver(schema),
  });

  const _clearForm = {
    name: '',
    description: '',
    category: [],
  };

  const { formState, getValues, reset, control, setValue } = methods;
  const { isValid } = formState;
  const { errors } = formState;
  const { t } = useTranslation('navigation');

  const resolveCategoryOptions = (categoryField, categoriesList) => {
    if (categoryField == null || categoryField === '') {
      return [];
    }

    const rawIds = Array.isArray(categoryField) ? categoryField : [categoryField];
    const ids = rawIds
      .map((item) => {
        if (item == null || item === '') {
          return null;
        }
        if (typeof item === 'object') {
          return item.id_cat ?? item.value ?? item.category ?? null;
        }
        return item;
      })
      .filter((id) => id !== null && id !== undefined && id !== '');

    return ids
      .map((id) =>
        categoriesList.find(
          (cat) => String(cat.value) === String(id) || Number(cat.value) === Number(id)
        )
      )
      .filter(Boolean);
  };

  const getAllCategories = async () => {
    const id = new_store.getState().loadz_auth?.AuthReducer?.user?.user_id;
    let categoriesList = [];
    try {
      const _categories = await client.post('/catmaterials/all', { id });

      if (_categories.data.message) {
        _showMessage(_categories.data.message, 'error');
        return categoriesList;
      }

      categoriesList = (_categories.data || []).map((e) => {
        return { value: e.id_cat, label: e.name_cat };
      });
      setCategories(categoriesList);
      return categoriesList;
    } catch (error) {
      categoriesList = [{ value: 0, label: 'Create categories' }];
      setCategories(categoriesList);
      return categoriesList;
    }
  };

  useEffect(() => {
    setOpen(isOpenModal);
  }, [isOpenModal]);

  useEffect(() => {
    getAllCategories();
  }, []);

  useEffect(() => {
    const loadMaterial = async () => {
      if (!isOpenModal) {
        return;
      }

      const categoriesList =
        categories.length > 0 ? categories : await getAllCategories();

      if (isEdit) {
        const materialId = info?.mt_id || info?.value;
        try {
          // Prefer material from /materials/all (includes category = id_cat)
          let material = info;
          if (materialId && info?.category == null && info?.categories == null) {
            const response = await client.get(`/materials/${materialId}`);
            material = response.data;
          }

          setValue('name', material?.name || info?.label || '');
          setValue('description', material?.description || '');
          setValue(
            'category',
            resolveCategoryOptions(
              material?.category ?? material?.categories,
              categoriesList
            )
          );
        } catch (error) {
          setValue('name', info?.label || info?.name || '');
          setValue('description', info?.description || '');
          setValue(
            'category',
            resolveCategoryOptions(info?.category ?? info?.categories, categoriesList)
          );
        }
      } else {
        reset(_clearForm);
      }
    };
    loadMaterial();
  }, [isOpenModal, info, categories]);

  useEffect(() => {
    if (createCategorie) {
      setValue('category', []);
    }
  }, [createCategorie]);

  const handleClose = () => {
    reset(_clearForm);
    setOpen(false);
    setIsOpenModal(false);
    if (setInfo) {
      setInfo(null);
    }
  };

  const _showMessage = (msg, color) => {
    dispatch(
      showMessage({
        message: (
          <>
            <Typography>{msg}</Typography>
          </>
        ),
        autoHideDuration: 6000,
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'right',
        },
        variant: color,
      })
    );
  };

  const handleCreateMaterial = async () => {
    const authUser = new_store.getState().loadz_auth?.AuthReducer?.user;
    const id = authUser?.user_id;
    const updated_by =
      authUser?.user_type == 'company' ? authUser?.user_id : authUser?.staff?.staff_u_id;
    const dateCreated = new Date();
    const active = true;
    const formValues = getValues();
    try {
      const response = await client.post(`/materials/`, {
        ...formValues,
        company_id: id,
        updated_by,
        dateCreated,
        active,
      });

      if (response.data.message) {
        _showMessage(
          'Failed to create the material. Please review your input and try again',
          'error'
        );
        return;
      }

      _showMessage('Material successfully created!', 'success');
      handleClose();
      getAllMaterials();
    } catch (error) {
      _showMessage('An unexpected error occurred. Please try again later', 'error');
      console.log(error);
    }
  };

  const handleUpdateMaterial = async () => {
    const materialId = info?.mt_id || info?.value;
    if (!materialId) {
      return;
    }
    const authUser = new_store.getState().loadz_auth?.AuthReducer?.user;
    const updated_by =
      authUser?.user_type == 'company' ? authUser?.user_id : authUser?.staff?.staff_u_id;
    try {
      const response = await client.put(`/materials/${materialId}`, {
        ...getValues(),
        mt_id: materialId,
        updated_by,
        updated_date: new Date().toISOString(),
      });

      if (response.data?.message && response.data?.error) {
        _showMessage('Something went wrong, please try again!', 'error');
        return;
      }

      _showMessage('Material successfully updated!', 'success');
      handleClose();
      getAllMaterials();
    } catch (error) {
      _showMessage('An unexpected error occurred. Please try again later', 'error');
      console.log(error);
    }
  };

  return (
    <div>
      <FuseMessage />
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>
          {isEdit ? t('EDIT_MATERIAL') || 'Edit Material' : t('ADD_NEW_MATERIAL')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {isEdit
              ? t('PLEASE_UPDATE_THIS_FORM_WITH_THE_INFORMATION_REQUESTED') ||
                'Please update this form with the information requested'
              : t('PLEASE_FILL_THIS_FORM_WITH_THE_INFORMATION_REQUESTED')}
          </DialogContentText>
          <br />
          <FormProvider {...methods}>
            <div className="flex -mx-6">
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    className="mt-8 mb-16"
                    error={!!errors.name}
                    required
                    helperText={errors?.name?.message}
                    label={t('NAME')}
                    autoFocus
                    id="name"
                    variant="outlined"
                    fullWidth
                  />
                )}
              />
            </div>

            <Controller
              name="category"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  className="mt-8 mb-16"
                  multiple
                  freeSolo={(value || []).length == 1 ? false : true}
                  options={categories}
                  value={value || []}
                  onChange={(event, newValue) => {
                    setCreateCategorie(false);
                    (newValue || []).map((r) => {
                      if (r.value == 0) {
                        setCreateCategorie(true);
                      }
                    });
                    onChange(newValue);
                  }}
                  fullWidth
                  getOptionLabel={(option) =>
                    typeof option === 'string' ? option : option?.label || ''
                  }
                  isOptionEqualToValue={(option, val) =>
                    String(option?.value) === String(val?.value)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={t('SELECT')}
                      label={t('CATEGORIES')}
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      helperText={
                        (value || []).length == 0
                          ? t('PLEASE_SELECT_A_CATEGORY_FOR_THIS_MATERIAL')
                          : ''
                      }
                    />
                  )}
                  limitTags={1}
                />
              )}
            />

            <div className="flex -mx-6">
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    className="mt-8 mb-16"
                    id="description"
                    label={t('DESCRIPTION')}
                    type="text"
                    multiline
                    rows={5}
                    variant="outlined"
                    fullWidth
                  />
                )}
              />
            </div>
          </FormProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('CANCEL')}</Button>
          <Button
            disabled={!isValid}
            onClick={isEdit ? handleUpdateMaterial : handleCreateMaterial}
          >
            {isEdit ? t('UPDATE') || 'Update' : t('CREATE')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default memo(ModalCreateMaterials);
